import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Text,
  Button,
  TextField,
  TextArea,
  Box,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  LoadingOrbit,
  Trash,
  DangerSection,
  DangerAction,
  Flex,
  PageHeader,
  SettingsSection,
  Select,
  type SelectOption,
} from '@chatsdet/ui';
import { useNavigate } from '@tanstack/react-router';
import {
  APPLICATION_TYPES,
  APPLICATION_TYPE_LABELS,
  type ApplicationType,
  isApplicationType,
} from '@chatsdet/types';
import { API_URL } from '../../../config';
import { parseApiJson } from '../../../lib/api-error';
import { isValidLoginPath } from './property-fields';
import { SectionSaveFooter } from './SectionSaveFooter';
import { usePropertySection, usePropertySettingsQuery } from './usePropertySection';

const toOption = (t: ApplicationType): SelectOption => ({ id: t, label: APPLICATION_TYPE_LABELS[t] });
// MCP is chosen when the application is created and cannot be switched to or from afterwards.
const NON_MCP_TYPE_OPTIONS = APPLICATION_TYPES.filter((t) => t !== 'mcp').map(toOption);
const MCP_TYPE_OPTIONS = [toOption('mcp')];

interface PropertySettingsTabProps {
  propertyId: string;
}

/**
 * General settings: what identifies this application. Infrastructure-shaped
 * settings (session state, environment variables, Release QA) live on the
 * Advanced tab.
 */
export function PropertySettingsTab({ propertyId }: PropertySettingsTabProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const propertyQ = usePropertySettingsQuery(propertyId);

  const details = usePropertySection({
    propertyId,
    select: (p) => ({
      name: p.name,
      type: p.type,
      description: p.description || '',
      loginPath: p.loginPath ?? '',
    }),
    toPatch: (d) => ({
      name: d.name,
      type: d.type,
      description: d.description,
      // Empty string clears the login path (sessions start at the environment URL)
      loginPath: d.loginPath || null,
    }),
  });

  const exploratory = usePropertySection({
    propertyId,
    select: (p) => ({
      defaultAdHocEnvironmentTemplateId: p.defaultAdHocEnvironmentTemplateId ?? null,
    }),
    toPatch: (d) => ({
      defaultAdHocEnvironmentTemplateId: d.defaultAdHocEnvironmentTemplateId,
    }),
  });

  // Eligible sandbox templates for the per-property default ad-hoc env.
  // Empty list ⇒ section renders a hint instead of an empty Select.
  const eligibleTemplatesQ = useQuery({
    queryKey: ['environment-templates', 'ad-hoc-eligible', propertyId],
    queryFn: async () => {
      const r = await fetch(
        `${API_URL}/org/environments/templates?eligibleFor=ad_hoc_testing&propertyId=${propertyId}&limit=100`,
        { credentials: 'include' }
      );
      if (!r.ok) throw new Error('Failed to fetch templates');
      const json = await r.json();
      return (json.data ?? []) as Array<{ id: string; name: string }>;
    },
  });

  const deleteProperty = useMutation({
    mutationFn: async () => {
      const r = await fetch(API_URL + '/org/properties/delete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ id: propertyId }),
      });
      return parseApiJson(r, 'Failed to delete application');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['properties'] });
      navigate({ to: '/applications' });
    },
  });

  const adHocTemplateOptions = React.useMemo<SelectOption[]>(
    () => [
      { id: '__none__', label: 'Live application URL (no sandbox)' },
      ...(eligibleTemplatesQ.data ?? []).map((t) => ({ id: t.id, label: t.name })),
    ],
    [eligibleTemplatesQ.data]
  );
  const hasEligibleAdHocTemplates = (eligibleTemplatesQ.data?.length ?? 0) > 0;

  if (propertyQ.isLoading || details.isLoading || exploratory.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  const loginPathInvalid = !isValidLoginPath(details.draft.loginPath);
  const nameMissing = details.draft.name.trim() === '';

  return (
    <Box className="space-y-4">
      <PageHeader
        size="section"
        title="General"
        description="Name this application and point tests at the right starting page."
      />

      <SettingsSection
        title="Application Details"
        description="Basic information about this application"
        footer={
          details.isDirty ? (
            <SectionSaveFooter
              isSaving={details.isSaving}
              canSave={!loginPathInvalid && !nameMissing}
              message={
                nameMissing
                  ? 'An application name is required'
                  : loginPathInvalid
                    ? 'Login path must be a relative path'
                    : undefined
              }
              onSave={details.save}
              onDiscard={details.discard}
            />
          ) : null
        }
      >
        {details.isSaving && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10">
            <LoadingOrbit size="lg" />
          </div>
        )}
        <TextField
          label="Application Name"
          value={details.draft.name}
          onChange={(v) => details.setDraft((d) => ({ ...d, name: v }))}
          isRequired
        />
        <Select
          label="Type"
          items={details.draft.type === 'mcp' ? MCP_TYPE_OPTIONS : NON_MCP_TYPE_OPTIONS}
          isDisabled={details.draft.type === 'mcp'}
          selectedKey={details.draft.type}
          onSelectionChange={(key) => {
            const next = String(key);
            if (isApplicationType(next)) details.setDraft((d) => ({ ...d, type: next }));
          }}
        />
        <TextArea
          label="Description"
          value={details.draft.description}
          onChange={(v) => details.setDraft((d) => ({ ...d, description: v }))}
          rows={2}
          autoExpand
        />
        <div>
          <TextField
            label="Login Path"
            value={details.draft.loginPath}
            onChange={(v) => details.setDraft((d) => ({ ...d, loginPath: v }))}
            placeholder="/login"
            description="Relative path of the login page, appended to each environment's URL. Leave empty to start logins at the environment URL. Environments can override this individually."
          />
          {loginPathInvalid && (
            <Text size="xs" className="text-destructive mt-1 text-pretty">
              Login path must be a relative path starting with "/" (e.g. /login), not a full URL.
            </Text>
          )}
        </div>
      </SettingsSection>

      <SettingsSection
        title="Exploratory Testing"
        description="Default environment used when starting a new exploratory test for this application."
        footer={
          exploratory.isDirty ? (
            <SectionSaveFooter
              isSaving={exploratory.isSaving}
              onSave={exploratory.save}
              onDiscard={exploratory.discard}
            />
          ) : null
        }
      >
        <Select
          label="Default environment"
          items={adHocTemplateOptions}
          selectedKey={exploratory.draft.defaultAdHocEnvironmentTemplateId ?? '__none__'}
          onSelectionChange={(key) =>
            exploratory.setDraft(() => ({
              defaultAdHocEnvironmentTemplateId:
                key && String(key) !== '__none__' ? String(key) : null,
            }))
          }
          isDisabled={eligibleTemplatesQ.isLoading}
        />
        {!eligibleTemplatesQ.isLoading && !hasEligibleAdHocTemplates && (
          <div className="space-y-1">
            <Text size="xs" className="text-muted-foreground text-pretty">
              No sandbox templates are currently eligible for sandbox-backed exploratory testing.
            </Text>
            <Text size="xs" className="text-muted-foreground text-pretty">
              A template must be verified and include an enabled credential-producing sandbox
              action. Use the template checklist&apos;s Ad Hoc Test Ready step to create and
              validate that action.
            </Text>
          </div>
        )}
      </SettingsSection>

      <DangerSection>
        <DangerAction
          title="Delete Application"
          description="Permanently delete this application and all associated data. This action cannot be undone."
        >
          <Button
            variant="destructive"
            onPress={() => setIsDeleteModalOpen(true)}
            iconLeft={<Trash size={14} />}
          >
            Delete Application
          </Button>
        </DangerAction>
      </DangerSection>

      <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <span />
        <Dialog title="Delete Application">
          <DialogContent>
            <Text>
              Are you sure you want to delete the application "
              <strong>{propertyQ.data?.name}</strong>"?
            </Text>
            <Text size="sm" className="text-muted-foreground mt-2 text-pretty">
              This action cannot be undone. All associated settings and credentials will be
              permanently removed.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setIsDeleteModalOpen(false)}
              isDisabled={deleteProperty.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={() => deleteProperty.mutate()}
              isDisabled={deleteProperty.isPending}
            >
              {deleteProperty.isPending ? 'Deleting...' : 'Delete Application'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Box>
  );
}
